import Checkbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/Checkbox';
import { FilterChip } from '@liveauctioneers/caterwaul-components/lib/FilterChip/FilterChip';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Trash } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { parseISO } from 'date-fns';
import styled, { useTheme } from 'styled-components';

import type { Facet } from '@/types/SearchFacet';

import useWindowSize from '@/hooks/useWindowSize';
import { useAppDispatch } from '@/redux/hooks';
import { submitDeleteSavedSearch } from '@/redux/modules/savedSearch';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { toFacetLabelPairs, toFacetString, toSavedSearchUrl } from '@/utils/savedSearchFacets';

import TableRow from './TableRow';

type SavedSearch = {
    facets: Facet[];
    lastProcessed: string;
    lastSent: string;
    lastStatus: string;
    savedSearchId: number;
    searchURL: string;
    sendAlert: boolean;
    userId: number;
};

type Props = {
    searches: SavedSearch[];
    toggleSavedSearchEmail: Function;
};

const SavedSearchTable = ({ searches = [], toggleSavedSearchEmail }: Props) => {
    const intl = useIntl();
    const { formatMessage } = intl;
    const { width } = useWindowSize();
    const dispatch = useAppDispatch();
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();

    const renderFilters = (facets: Facet[], savedSearchUrl: string) => {
        const searchLabels = toFacetLabelPairs(facets, intl);
        const filters = searchLabels.map((title) => (
            <FilterChip key={title}>
                <StyledLink
                    className="line-clamp-1 truncate whitespace-normal"
                    route
                    title={title}
                    to={savedSearchUrl}
                >
                    {title}
                </StyledLink>
            </FilterChip>
        ));

        return filters;
    };

    const mapSavedSearch = (search: SavedSearch) => {
        const date = search.lastSent.length ? parseISO(search.lastSent) : null;
        const savedSearchUrl = search?.searchURL || toSavedSearchUrl(search.facets);
        const title = cleanSearchTerms(toFacetString(search.facets, intl));

        return (
            <StyledTableRow
                email={
                    <StyledCheckbox
                        checked={search.sendAlert}
                        label={formatMessage({ id: 'notificationsPage.toggleEmailNotifcations' }, { name: title })}
                        labelDisplay=""
                        name="emailCheckbox"
                        onClick={() => toggleSavedSearchEmail(search.savedSearchId)}
                    />
                }
                frequency={
                    width > tabletNarrowWidthNum &&
                    (search.lastSent === '0001-01-01T00:00:00' || search.lastSent === '' ? (
                        <FormattedMessage id="never" />
                    ) : (
                        shortFormatDate(date)
                    ))
                }
                key={search.savedSearchId}
                type={
                    <SearchTermContainer>
                        <IconButton
                            className="mr-sm"
                            icon={Trash}
                            onClick={() => dispatch(submitDeleteSavedSearch(search))}
                            size="sm"
                        />
                        <div className="flex max-w-full flex-wrap gap-sm overflow-hidden">
                            {renderFilters(search.facets, savedSearchUrl)}
                        </div>
                    </SearchTermContainer>
                }
            />
        );
    };

    return (
        <>
            <TableRow
                email={
                    <HeaderCaptionText color="secondary">
                        <FormattedMessage id="notificationsPage.table.header.emailAlert" />
                    </HeaderCaptionText>
                }
                frequency={
                    <HeaderCaptionText color="secondary">
                        <FormattedMessage id="notificationsPage.table.header.lastNewResult" />
                    </HeaderCaptionText>
                }
                type={
                    <HeaderCaptionText color="secondary">
                        <FormattedMessage id="notificationsPage.table.header.searchTerm" />
                    </HeaderCaptionText>
                }
            />
            {searches.map(mapSavedSearch)}
        </>
    );
};

export default SavedSearchTable;

const HeaderCaptionText = styled(Caption)`
    text-transform: uppercase;
`;

const StyledTableRow = styled(TableRow)`
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        > :nth-child(1) {
            grid-column: 1 / 4;
        }
    }
`;

const SearchTermContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    vertical-align: center;
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: ${({ theme }) => theme.colors.grey100};
`;

const StyledCheckbox = styled(Checkbox)`
    margin-bottom: 0;
`;
